// Optional biometric (Face ID / Touch ID / fingerprint) lock.
//
// This is a LOCAL access gate layered on top of the existing persisted
// session (see authToken.js). It does NOT change backend auth — the session
// token remains the source of truth. When the user enables biometric lock,
// the app prompts for biometrics on cold start / resume before exposing the
// app, instead of requiring a fresh login.
//
// Web (non-Capacitor) builds gracefully no-op: isBiometricAvailable() returns
// false, so the toggle is hidden and the gate never engages.

import { Capacitor } from '@capacitor/core';

const ENABLED_KEY = 'biometric_lock_enabled';

let _native = null; // resolved { BiometricAuth } or false
let _prefs = null;   // resolved Preferences or false

const isNative = () => {
  try { return !!Capacitor?.isNativePlatform?.(); } catch { return false; }
};

const getAuth = async () => {
  if (_native) return _native;         // resolved plugin
  if (_native === false) return null;   // confirmed web
  if (!isNative()) { _native = false; return null; }
  try {
    // Don't cache false on a slow/failed first import — one slow cold-start
    // import must not permanently hide the biometric toggle. Retry next call.
    const mod = await Promise.race([
      import('@aparajita/capacitor-biometric-auth'),
      new Promise((_, rej) => setTimeout(() => rej(new Error('bio import timeout')), 5000)),
    ]);
    _native = mod.BiometricAuth || null;
    return _native;
  } catch { return null; }
};

const getPrefs = async () => {
  if (_prefs) return _prefs;            // resolved Preferences
  if (_prefs === false) return null;     // confirmed web
  if (!isNative()) { _prefs = false; return null; }
  try {
    const mod = await Promise.race([
      import('@capacitor/preferences'),
      new Promise((_, rej) => setTimeout(() => rej(new Error('prefs import timeout')), 5000)),
    ]);
    _prefs = mod.Preferences || null;
    return _prefs;
  } catch { return null; }
};

/** True only on native devices that actually have biometrics enrolled. */
export const isBiometricAvailable = async () => {
  const auth = await getAuth();
  if (!auth) return false;
  const probe = async () => {
    const info = await auth.checkBiometry();
    return !!(info?.isAvailable || info?.strongBiometryIsAvailable);
  };
  try {
    return await probe();
  } catch {
    // The bridge occasionally isn't ready on the very first call after a cold
    // start — retry once before giving up.
    try { await new Promise(r => setTimeout(r, 500)); return await probe(); }
    catch { return false; }
  }
};

/** Human label for the available biometry (Face ID / Touch ID / Fingerprint). */
export const getBiometryLabel = async () => {
  const auth = await getAuth();
  if (!auth) return 'Biometrics';
  try {
    const info = await auth.checkBiometry();
    const t = info?.biometryType;
    // BiometryType enum: 1=touchId, 2=faceId, 3=fingerprint, 4=faceAuth, 5=iris
    if (t === 2 || t === 4) return 'Face ID';
    if (t === 1) return 'Touch ID';
    if (t === 3) return 'Fingerprint';
    return 'Biometrics';
  } catch { return 'Biometrics'; }
};

/** Has the user opted into the biometric lock? (survives cold start) */
export const isBiometricEnabled = async () => {
  const prefs = await getPrefs();
  if (!prefs) return false;
  try {
    const { value } = await prefs.get({ key: ENABLED_KEY });
    return value === 'true';
  } catch { return false; }
};

/** Trigger the native biometric prompt. Resolves true on success. */
export const verifyBiometric = async (reason = 'Unlock Aayi') => {
  const auth = await getAuth();
  if (!auth) return true; // web / unsupported → never block
  try {
    await auth.authenticate({
      reason,
      cancelTitle: 'Cancel',
      allowDeviceCredential: true,
      iosFallbackTitle: 'Use passcode',
      androidTitle: 'Aayi',
      androidSubtitle: reason,
      androidConfirmationRequired: false,
    });
    return true;
  } catch { return false; }
};

/** Turn the lock on — requires a successful verification first. */
export const enableBiometric = async () => {
  if (!(await isBiometricAvailable())) return false;
  if (!(await verifyBiometric('Enable biometric unlock'))) return false;
  const prefs = await getPrefs();
  if (prefs) { try { await prefs.set({ key: ENABLED_KEY, value: 'true' }); } catch { /* noop */ } }
  return true;
};

/** Turn the lock off. */
export const disableBiometric = async () => {
  const prefs = await getPrefs();
  if (prefs) { try { await prefs.set({ key: ENABLED_KEY, value: 'false' }); } catch { /* noop */ } }
  return true;
};
